/**
 * Запретные районы на карте: начертание и строки карточки.
 *
 * Чистая логика без MapLibre и React. Укладка — `bansLayer.ts`, запрос по
 * кадру — `useBansLayer.ts`.
 *
 * Слой ТОТ ЖЕ, что промысловые районы (решение владельца 18.09.2026): районы
 * отвечают «по каким правилам тут ловят», запреты — «где ловить нельзя».
 *
 * АКТИВНОСТЬ ПРИЕЗЖАЕТ С СЕРВЕРА (`active`, `applies`), а не считается здесь:
 * правило «с 1 ноября по 15 февраля» живёт в одной копии, и вторая во фронте
 * разъехалась бы с ней на переходе через Новый год.
 *
 * ДВА НАЧЕРТАНИЯ, И ОБА НУЖНЫ. Запрет, действующий сегодня для выбранного
 * вида, — заливка и сплошная кромка: здесь ловить нельзя. Остальные — бледный
 * пунктир без заливки: запрет есть, но не сейчас или не для этого вида.
 * Спрятав их, мы показали бы капитану воду свободной накануне закрытия.
 */
import { mapColorOf } from '../brand/mapColors'
import { OVERLAY_PREFIX } from './layerOrder'

export const banLayerId = (role: string): string => `${OVERLAY_PREFIX}fb-${role}`

export const banColor = (): string => mapColorOf('fishing-ban')

/** Ниже этого зума номера пунктов сливаются в кашу. */
export const BAN_LABEL_MIN_ZOOM = 6

export const BAN_DASH: [number, number] = [2, 2]

export interface BanPeriod {
  from: string
  to: string
}

export interface BanProps {
  code: string
  point: string
  title: string
  quote: string
  species: string[]
  except_species: string[]
  gears: string[]
  periods: BanPeriod[]
  exceptions: string | null
  note: string | null
  active: boolean
  applies: boolean
  /** Статус вычислителя норм (`src/rules/evaluate.py`); нет — у запрета нет нормы. */
  verdict?: BanVerdict | null
  label?: [number, number]
}

export type VerdictStatus =
  | 'applies'
  | 'excepted'
  | 'depends'
  | 'not_in_period'
  | 'not_for_species'
  | 'not_for_gear'
  | 'not_applicable'

export interface BanVerdict {
  status: VerdictStatus
  /** Поля запроса, которых карта не знает, — кодами (`bans.need.<код>`). */
  missing: string[]
  /** Условия приказа, не легшие в поля, — словами приказа (текст на русском). */
  conditions: string[]
  excepted_by: string | null
  alt_status: VerdictStatus | null
  ambiguous: boolean
}

const STATUS_KEYS: Record<VerdictStatus, string> = {
  applies: 'bans.card.inForce',
  excepted: 'bans.card.excepted',
  depends: 'bans.card.depends',
  not_in_period: 'bans.card.notNow',
  not_for_species: 'bans.card.notForSpecies',
  not_for_gear: 'bans.card.notApplicable',
  not_applicable: 'bans.card.notApplicable',
}

export function missingText(code: string, t: (key: string) => string): string {
  return t(`bans.need.${code}`)
}

/**
 * Статус словами. «Действует» ставится ТОЛЬКО когда вычислитель сказал
 * `applies`: зависящее от длины судна или орудия называется «зависит от …»,
 * снятое исключением — «снято: <исключение>».
 */
export function verdictText(v: BanVerdict, t: (key: string) => string): string {
  const need = [...v.missing.map(m => missingText(m, t)), ...v.conditions]
  let out = v.status === 'depends' && need.length
    ? `${t('bans.card.dependsOn')}: ${need.join(', ')}`
    : t(STATUS_KEYS[v.status])
  if (v.status === 'excepted' && v.excepted_by) out += `: ${v.excepted_by}`
  if (v.alt_status && v.alt_status !== v.status)
    out += ` (${t('bans.card.ambiguous')}: ${t(STATUS_KEYS[v.alt_status])})`
  return out
}

/** Выражение MapLibre «запрет в силе для этого кадра». */
export const BAN_IN_FORCE: unknown[] = ['all', ['get', 'active'], ['get', 'applies']]

/** «07-01» → «1.07»: день и месяц — как в приказе, без года. */
export function periodText(p: BanPeriod): string {
  const day = (md: string) => {
    const [m, d] = md.split('-').map(Number)
    return `${d}.${String(m).padStart(2, '0')}`
  }
  return `${day(p.from)}–${day(p.to)}`
}

/**
 * Строка карточки про один запрет: пункт, что запрещено, кому и когда.
 *
 * Статус идёт ПЕРВЫМ: капитан открывает карточку, чтобы узнать, можно ли тут
 * ловить сейчас, а не чтобы прочитать приказ.
 */
export function banLine(b: BanProps, t: (key: string) => string): string {
  const status = b.verdict
    ? verdictText(b.verdict, t)
    : !b.applies
      ? t('bans.card.notForSpecies')
      : b.active
        ? t('bans.card.inForce')
        : t('bans.card.notNow')
  // «п.» — только номеру пункта приказа (`23.2.1 г`). Источник с другим
  // устройством пишет пункт целиком: «Art. 178», «RD 23/96» (правила Омана).
  const point = /^\d/.test(b.point) ? `${t('bans.card.point')} ${b.point}` : b.point
  const bits = [point, b.title]
  if (b.gears.length) bits.push(`${t('bans.card.gears')}: ${b.gears.join(', ')}`)
  if (b.periods.length) bits.push(b.periods.map(periodText).join(', '))
  // Снятому исключением «кроме» уже названо в статусе.
  if (b.exceptions && b.verdict?.status !== 'excepted')
    bits.push(`${t('bans.card.except')}: ${b.exceptions}`)
  return `${status} — ${bits.join(' · ')}`
}

export interface BanAnswer {
  status: 'bans' | 'none' | 'unknown'
  bans?: BanProps[]
}

/**
 * Строки карточки отсчёта. ТРИ ответа, как у районов: `none` — запретов здесь
 * нет, и это надо произнести; `unknown` — не смотрели, и молчание честнее.
 * Действующие для вида — первыми.
 */
export function banLines(
  a: BanAnswer | null | undefined,
  t: (key: string) => string,
): string[] | null {
  if (!a || a.status === 'unknown') return null
  if (a.status === 'none' || !a.bans?.length) return [t('bans.status.none')]
  const rank = (b: BanProps) => {
    if (b.verdict) return b.verdict.status === 'applies' ? 0 : b.verdict.status === 'depends' ? 1 : 2
    return b.applies && b.active ? 0 : b.applies ? 1 : 2
  }
  return [...a.bans].sort((x, y) => rank(x) - rank(y)).map(b => banLine(b, t))
}

/** Подписи — номер пункта в точке, посчитанной сервером по видимой части. */
export function banLabels(data: GeoJSON.FeatureCollection): GeoJSON.FeatureCollection {
  const features = (data.features ?? [])
    .map(f => {
      const p = f.properties as unknown as BanProps | null
      if (!p?.label || p.label.length !== 2) return null
      return {
        type: 'Feature' as const,
        properties: { _label: p.point, active: p.active, applies: p.applies },
        geometry: { type: 'Point' as const, coordinates: p.label },
      }
    })
    .filter((f): f is NonNullable<typeof f> => f !== null)
  return { type: 'FeatureCollection', features }
}
